import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X, User, MessageSquare, Clock, Search, ArrowUpDown } from "lucide-react";

import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import {
  approveContactRequest,
  listOwnerContactRequests,
  rejectContactRequest,
  STATUS_CLASS,
  STATUS_LABEL,
  type ContactRequestStatus,
  type ContactRequestView,
} from "@/lib/contact-unlock";

const TABS: readonly (ContactRequestStatus | "all")[] = [
  "all",
  "pending",
  "approved",
  "rejected",
  "expired",
  "cancelled",
];

const SORTS = [
  { value: "date_desc", label: "Newest first" },
  { value: "date_asc", label: "Oldest first" },
  { value: "customer_asc", label: "Customer A–Z" },
  { value: "customer_desc", label: "Customer Z–A" },
  { value: "listing_asc", label: "Listing A–Z" },
  { value: "listing_desc", label: "Listing Z–A" },
] as const;

type SortKey = (typeof SORTS)[number]["value"];

export const Route = createFileRoute("/_authenticated/partner/contact-requests")({
  head: () => ({
    meta: [
      { title: "Contact requests · Landoo" },
      {
        name: "description",
        content: "Approve or decline customer contact unlock requests for your listings.",
      },
    ],
  }),
  component: PartnerContactRequestsPage,
});

function PartnerContactRequestsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<ContactRequestStatus | "all">("pending");
  const [modal, setModal] = useState<{
    row: ContactRequestView;
    action: "approve" | "reject";
  } | null>(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("date_desc");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["partner", "contact-requests", user?.id],
    queryFn: () => listOwnerContactRequests(user!.id),
    enabled: !!user,
  });

  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.counterpartyName ?? "").toLowerCase().includes(q) ||
        (r.listingTitle ?? "").toLowerCase().includes(q),
    );
  }, [rows, query]);

  const counts = useMemo(() => {
    const acc: Record<string, number> = { all: searched.length };
    for (const s of TABS)
      if (s !== "all") acc[s] = searched.filter((r) => r.effectiveStatus === s).length;
    return acc;
  }, [searched]);

  const visible = useMemo(() => {
    const list = tab === "all" ? searched : searched.filter((r) => r.effectiveStatus === tab);
    const byText = (a: string | null, b: string | null) =>
      (a ?? "").localeCompare(b ?? "", undefined, { sensitivity: "base" });
    const sorted = [...list];
    sorted.sort((a, b) => {
      switch (sort) {
        case "date_asc":
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case "customer_asc":
          return byText(a.counterpartyName, b.counterpartyName);
        case "customer_desc":
          return byText(b.counterpartyName, a.counterpartyName);
        case "listing_asc":
          return byText(a.listingTitle, b.listingTitle);
        case "listing_desc":
          return byText(b.listingTitle, a.listingTitle);
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });
    return sorted;
  }, [searched, tab, sort]);

  return (
    <DashboardShell title="Contact requests" role="Partner" items={PARTNER_NAV}>
      <p className="mb-3 text-xs text-muted-foreground">
        Customers can request your contact when it's hidden. You choose who to unlock — access lasts
        7 days from approval.
      </p>

      <div className="mb-3 flex flex-col gap-2 sm:flex-row">
        <label className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search customer name or listing title…"
            className="w-full rounded-xl border border-border bg-card py-2 pl-9 pr-3 text-sm outline-none focus:border-brand"
          />
        </label>
        <label className="relative sm:w-56">
          <ArrowUpDown className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="w-full appearance-none rounded-xl border border-border bg-card py-2 pl-9 pr-3 text-sm outline-none focus:border-brand"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {TABS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setTab(s)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
              tab === s
                ? "bg-brown-dark text-primary-foreground"
                : "border border-border bg-card text-brown-dark"
            }`}
          >
            {s === "all" ? "All" : STATUS_LABEL[s]}
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                tab === s ? "bg-white/20" : "bg-secondary text-brown-dark"
              }`}
            >
              {counts[s] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading contact requests…</p>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          No contact requests in this view.
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((r) => (
            <RequestCard
              key={r.id}
              row={r}
              onApprove={() => setModal({ row: r, action: "approve" })}
              onReject={() => setModal({ row: r, action: "reject" })}
            />
          ))}
        </div>
      )}

      {modal && (
        <ResponseDialog
          row={modal.row}
          action={modal.action}
          onClose={() => setModal(null)}
          onDone={() => {
            setModal(null);
            qc.invalidateQueries({ queryKey: ["partner", "contact-requests", user?.id] });
          }}
        />
      )}
    </DashboardShell>
  );
}

function RequestCard({
  row,
  onApprove,
  onReject,
}: {
  row: ContactRequestView;
  onApprove: () => void;
  onReject: () => void;
}) {
  const canAct = row.effectiveStatus === "pending";
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span
          className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${STATUS_CLASS[row.effectiveStatus]}`}
        >
          {STATUS_LABEL[row.effectiveStatus]}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {new Date(row.created_at).toLocaleString()}
        </span>
      </div>
      <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
        <User className="h-3.5 w-3.5" /> {row.counterpartyName ?? "Customer"}
      </div>
      <div className="mt-1 text-sm font-semibold text-brown-dark">
        {row.listingTitle ?? "Listing"}
      </div>
      {row.message && (
        <div className="mt-2 flex items-start gap-2 rounded-xl bg-secondary/60 p-2.5 text-xs text-brown-dark">
          <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span>{row.message}</span>
        </div>
      )}
      {row.response_note && (
        <p className="mt-1 text-[11px] text-muted-foreground">Your note: {row.response_note}</p>
      )}
      {row.effectiveStatus === "approved" && row.expires_at && (
        <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <Clock className="h-3 w-3" /> Access ends {new Date(row.expires_at).toLocaleDateString()}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Link
          to="/property/$id"
          params={{ id: row.listing_id }}
          className="rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-brown-dark"
        >
          View listing
        </Link>
        {canAct && (
          <>
            <button
              type="button"
              onClick={onApprove}
              className="inline-flex items-center gap-1 rounded-full bg-rent px-3 py-1.5 text-xs font-semibold text-white"
            >
              <Check className="h-3.5 w-3.5" /> Approve
            </button>
            <button
              type="button"
              onClick={onReject}
              className="inline-flex items-center gap-1 rounded-full border border-coral/40 px-3 py-1.5 text-xs font-semibold text-coral"
            >
              <X className="h-3.5 w-3.5" /> Reject
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ResponseDialog({
  row,
  action,
  onClose,
  onDone,
}: {
  row: ContactRequestView;
  action: "approve" | "reject";
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      action === "approve" ? approveContactRequest(row, note) : rejectContactRequest(row, note),
    onSuccess: () => {
      toast.success(
        action === "approve" ? "Contact unlocked for the customer." : "Request declined.",
      );
      onDone();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Could not update"),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 sm:items-center">
      <div className="w-full max-w-md rounded-2xl bg-card p-5 shadow-lg">
        <h3 className="text-sm font-bold text-brown-dark">
          {action === "approve" ? "Approve contact request" : "Reject contact request"}
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          {action === "approve"
            ? "The customer will see your phone and WhatsApp numbers for 7 days."
            : "The customer will see this note (optional)."}
        </p>
        <textarea
          rows={3}
          maxLength={280}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={action === "approve" ? "Optional welcome note…" : "Reason (optional)…"}
          className="mt-3 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand"
        />
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-border py-2 text-xs font-semibold text-brown-dark"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
            className={`flex-1 rounded-xl py-2 text-xs font-semibold text-white shadow-soft disabled:opacity-60 ${
              action === "approve" ? "bg-rent" : "bg-coral"
            }`}
          >
            {mutation.isPending ? "Saving…" : action === "approve" ? "Approve" : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
}
